import { useEffect, useRef, useState, useMemo } from "react";
import { Box, Button } from "@mui/material";
import { useTranslation } from "react-i18next";

import { format, startOfWeek } from "date-fns";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";

import WeekPicker from "../Pickers/WeekPicker.jsx";
import ConsumptionBarChart from "../Consumption Insights/ConsumptionBarchart.jsx";
import { fetchUserConsumptionData } from "../../../services/Statistics/Liquids/consumptionEndpoint.js";
import { captureAndDownloadCharts } from "../CaptureCharts/captureAndDownloadCharts.js";
import { calculateWeeksAndMonthsWithData } from "../../../utils/filterDataByDate.jsx";

export default function LiquidsComponent({
  selectedUser,
  tabsValue,
  setLoading,
}) {
  const [consumptionData, setConsumptionData] = useState([]);
  const consumptionInsights = useRef(null);

  const today = new Date();
  const [currentWeek, setCurrentWeek] = useState(today);
  const [todayClickFunc, setTodayClickFunc] = useState(null);

  const { t } = useTranslation("statistics");

  useEffect(() => {
    fetchUserConsumptionData(selectedUser, setConsumptionData);
  }, []);

  const handleTodayClickParent = (func) => {
    if (!todayClickFunc) {
      setTodayClickFunc(() => func);
    }
  };
  const weekStart = startOfWeek(currentWeek, { weekStartsOn: 1 });

  const isCurrentWeek =
    weekStart.getTime() === startOfWeek(today, { weekStartsOn: 1 }).getTime();

  const handleWeekChange = (week) => {
    setCurrentWeek(week);
    // console.log("Updated selected week:", week);
  };

  const filteredLiquidsData = consumptionData.filter((data) => {
    return (
      data.water !== null ||
      data.coffee !== null ||
      data.refreshment !== null ||
      data.tea !== null ||
      data.alcohol !== null
    );
  });
  const weeksMonthsWithData =
    calculateWeeksAndMonthsWithData(filteredLiquidsData);
  // console.log("weeksMonthsWithData in liquidsComponent: ", weeksMonthsWithData);

  const weekStartISO = (d) =>
    format(startOfWeek(d, { weekStartsOn: 1 }), "yyyy-MM-dd");

  const weeksWithDataSet = useMemo(() => {
    const s = new Set();
    for (const row of consumptionData) {
      const day = new Date(row.date); // row.date is 'yyyy-MM-dd'
      s.add(weekStartISO(day));
    }
    return s;
  }, [consumptionData]);

  const hasAnyData = consumptionData.length > 0;
  const todayWeekISO = weekStartISO(today);
  const todayWeekHasData = weeksWithDataSet.has(todayWeekISO);

  const showTodayButton = !isCurrentWeek && (!hasAnyData || todayWeekHasData);
  return (
    <LocalizationProvider dateAdapter={AdapterDateFns}>
      <Box sx={{ display: "flex", flexDirection: "column" }}>
        <WeekPicker
          today={today}
          currentCompWeek={currentWeek}
          sendDataToParent={handleTodayClickParent}
          onWeekChange={handleWeekChange}
          weeksWithData={weeksMonthsWithData.weeksWithData}
          monthsWithData={weeksMonthsWithData.monthsWithData}

        />
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
          }}
        >
          <Button
            onClick={() =>
              captureAndDownloadCharts(
                "Liquid",
                tabsValue,
                { consumptionInsights },
                setLoading
              )
            }
            sx={{
              maxWidth: "210px",
              "&:hover": {
                backgroundColor: "white",
                color: "rgb(2, 178, 175)",
              },
            }}
          >
            {t("btn.downloadChart")}
          </Button>
        </Box>
        {showTodayButton ? (
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
            }}
          >
            <Button
              sx={{
                maxWidth: "210px",
                backgroundColor: "transparent",
                "&:hover": {
                  backgroundColor: "white",
                  color: "rgb(2, 178, 175)",
                },
              }}
              onClick={todayClickFunc}
            >
              {t("btn.today")}
            </Button>
          </Box>
        ) : (
          <Button
            sx={{
              visibility: "hidden",
            }}
          >
            {t("btn.today")}
          </Button>
        )}

        <Box
          sx={{
            display: "flex",
            flexDirection: {
              xs: "column",
              sm: "column",
              md: "row",
              lg: "row",
            },
            gap: "10px",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <ConsumptionBarChart
            consumptionData={consumptionData}
            selectedWeek={currentWeek}
            consumptionInsights={consumptionInsights}
          />
        </Box>
      </Box>
    </LocalizationProvider>
  );
}
